import React, { useState, useEffect } from "react";
import { ChevronLeft, Plus, Trash2, X, Camera } from "lucide-react";
import supabase from "../supabaseClient";

const BULK_TIME_OPTIONS = (() => {
  const opts = [{ label: "No time (TBD)", value: "" }];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const hr = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const ampm = h < 12 ? "AM" : "PM";
      opts.push({ label: `${hr}:${String(m).padStart(2, "0")} ${ampm}`, value: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}` });
    }
  }
  return opts;
})();

const DURATION_OPTIONS = [
  { label: "No default", value: "" },
  { label: "30 min", value: 30 },
  { label: "45 min", value: 45 },
  { label: "1 hour", value: 60 },
  { label: "1h 15min", value: 75 },
  { label: "1h 30min", value: 90 },
  { label: "2 hours", value: 120 },
];

const REMINDER_OPTIONS = [
  { label: "No reminder", value: "" },
  { label: "1 hour before", value: 1 },
  { label: "2 hours before", value: 2 },
  { label: "3 hours before", value: 3 },
  { label: "12 hours before", value: 12 },
  { label: "24 hours before", value: 24 },
  { label: "48 hours before", value: 48 },
];

const fmtDuration = (min) => {
  if (!min) return null;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
};

const addMinutes = (timeStr, minutes) => {
  if (!timeStr || !minutes) return "";
  const [h, m] = timeStr.split(":").map(Number);
  const total = h * 60 + m + parseInt(minutes);
  const newH = Math.floor(total / 60) % 24;
  const newM = total % 60;
  return `${String(newH).padStart(2, "0")}:${String(newM).padStart(2, "0")}`;
};

const PRESET_COLORS = [
  "#ef4444", "#f97316", "#eab308", "#22c55e",
  "#3b82f6", "#8b5cf6", "#ec4899", "#6b7280",
];

const SPORT_EMOJIS = {
  Baseball: "⚾", Softball: "🥎", Soccer: "⚽", Basketball: "🏀",
  Football: "🏈", Hockey: "🏒", Lacrosse: "🥍", Tennis: "🎾",
  Swimming: "🏊", Track: "🏃", Volleyball: "🏐", Wrestling: "🤼",
};

const getSportEmoji = (sport) => SPORT_EMOJIS[sport] || "🏆";

const fmtDate = (dateStr) => {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric",
  });
};

const fmtTime = (timeStr) => {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(":").map(Number);
  const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const ampm = h < 12 ? "AM" : "PM";
  return `${hour}:${String(m).padStart(2, "0")} ${ampm}`;
};

// ── Member helpers ─────────────────────────────────────────────────────────
const MemberCheckboxes = ({ members, selectedIds, onChange }) => (
  <div className="border border-gray-200 rounded-lg p-2 space-y-1 max-h-32 overflow-y-auto bg-white">
    {members.map((m) => (
      <label key={m.id} className="flex items-center gap-2 cursor-pointer p-1 hover:bg-gray-50 rounded touch-manipulation">
        <input
          type="checkbox"
          checked={selectedIds.includes(m.id)}
          onChange={() => onChange(
            selectedIds.includes(m.id)
              ? selectedIds.filter((id) => id !== m.id)
              : [...selectedIds, m.id]
          )}
          className="rounded w-4 h-4"
        />
        <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: m.color }} />
        <span className="text-sm">{m.name}</span>
      </label>
    ))}
  </div>
);

const MemberDots = ({ members, memberIds }) => {
  if (!memberIds?.length) return <span className="text-sm text-gray-400">None</span>;
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {memberIds.map((id) => {
        const m = members.find((m) => m.id === id);
        return m ? (
          <span key={id} className="flex items-center gap-1 text-sm text-gray-700">
            <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: m.color }} />
            {m.name}
          </span>
        ) : null;
      })}
    </div>
  );
};

// ── Edit Game Modal ────────────────────────────────────────────────────────
const EditGameModal = ({ game, onClose, onSave, onCancelGame, onRestoreGame, onDeleteGame, venues, gameDurationMinutes, members = [], defaultMemberIds = [], defaultReminderHours }) => {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);
  const [title, setTitle] = useState(game.title || "");
  const [date, setDate] = useState(game.date || "");
  const [time, setTime] = useState(game.time ? game.time.substring(0, 5) : "");
  const [allDay, setAllDay] = useState(game.all_day || !game.time);
  const [venueId, setVenueId] = useState(game.venue_id ? String(game.venue_id) : "");
  const [location, setLocation] = useState(game.location || "");
  const [eventType, setEventType] = useState(game.event_type || "game");
  const [memberIds, setMemberIds] = useState(game.member_ids?.length ? game.member_ids : defaultMemberIds);
  const [reminderHoursBefore, setReminderHoursBefore] = useState(
    game.reminder_hours_before != null ? String(game.reminder_hours_before) : ""
  );
  const initEnd = game.end_time
    ? game.end_time.substring(0, 5)
    : (game.time && gameDurationMinutes ? addMinutes(game.time.substring(0, 5), gameDurationMinutes) : "");
  const [endTime, setEndTime] = useState(initEnd);
  const [repeat, setRepeat] = useState(game.repeat || "");
  const [repeatEndDate, setRepeatEndDate] = useState(game.repeat_end_date || "");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [cancelingGame, setCancelingGame] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  useEffect(() => {
    if (!game?.id) return;
    const load = async () => {
      const { data } = await supabase
        .from("event_photos")
        .select("*")
        .eq("event_id", game.id)
        .order("created_at");
      if (data) {
        const withUrls = await Promise.all(
          data.map(async (p) => {
            const { data: signed } = await supabase.storage
              .from("event-photos")
              .createSignedUrl(p.storage_path, 3600);
            return { ...p, url: signed?.signedUrl || "" };
          })
        );
        setPhotos(withUrls);
      }
    };
    load();
  }, [game?.id]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const path = `${game.id}/${Date.now()}_${file.name}`;
      const { error: uploadError } = await supabase.storage.from("event-photos").upload(path, file);
      if (uploadError) throw uploadError;
      const { data: row, error: dbError } = await supabase
        .from("event_photos")
        .insert({ event_id: game.id, storage_path: path })
        .select()
        .single();
      if (dbError) throw dbError;
      const { data: signed } = await supabase.storage.from("event-photos").createSignedUrl(path, 3600);
      setPhotos((prev) => [...prev, { ...row, url: signed?.signedUrl || "" }]);
    } catch (err) {
      alert("Error uploading photo: " + err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const deletePhoto = async (photo) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await supabase.storage.from("event-photos").remove([photo.storage_path]);
      await supabase.from("event_photos").delete().eq("id", photo.id);
      setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
    } catch (err) {
      alert("Error deleting photo: " + err.message);
    }
  };

  const handleVenueSelect = (id) => {
    setVenueId(id);
    if (id) {
      const v = venues.find((v) => String(v.id) === id);
      if (v) setLocation(v.address || "");
    }
  };

  const buildSaveData = (extra = {}) => ({
    title: title.trim(),
    date,
    time: allDay ? null : time || null,
    end_time: allDay ? null : endTime || null,
    all_day: allDay,
    location: location.trim() || null,
    venue_id: venueId ? parseInt(venueId) : null,
    event_type: eventType,
    member_ids: memberIds.length ? memberIds : null,
    member_id: null,
    reminder_hours_before: reminderHoursBefore !== "" ? parseInt(reminderHoursBefore) : null,
    repeat: repeat || null,
    repeat_end_date: repeat && repeatEndDate ? repeatEndDate : null,
    ...extra,
  });

  const handleSave = () => {
    if (!title.trim() || !date) return;
    onSave(buildSaveData());
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-4 my-auto">
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-semibold text-gray-800">Edit Event</h4>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 touch-manipulation">
            <X size={20} />
          </button>
        </div>
        <div className="space-y-3">
          {/* Event type */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setEventType("game")}
              className={`flex-1 py-1.5 rounded-lg text-sm font-medium border-2 touch-manipulation ${
                eventType === "game" ? "border-purple-500 bg-purple-50 text-purple-700" : "border-gray-200 text-gray-500"
              }`}
            >
              🏆 Game
            </button>
            <button
              type="button"
              onClick={() => setEventType("practice")}
              className={`flex-1 py-1.5 rounded-lg text-sm font-medium border-2 touch-manipulation ${
                eventType === "practice" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-gray-200 text-gray-500"
              }`}
            >
              🏃 Practice
            </button>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer touch-manipulation">
            <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} className="rounded w-4 h-4" />
            <span className="text-sm text-gray-700">All day</span>
          </label>
          {!allDay && (
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">Start time</label>
                <select value={time} onChange={(e) => {
                  setTime(e.target.value);
                  if (gameDurationMinutes && e.target.value) setEndTime(addMinutes(e.target.value, gameDurationMinutes));
                }} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500">
                  {BULK_TIME_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label || "No time"}</option>)}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">End time</label>
                <select value={endTime} onChange={(e) => setEndTime(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500">
                  {BULK_TIME_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label || "No time"}</option>)}
                </select>
              </div>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Repeat</label>
            <select value={repeat} onChange={(e) => setRepeat(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500">
              <option value="">No repeat</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Every 2 weeks</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          {repeat && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Repeat until</label>
              <input type="date" value={repeatEndDate} onChange={(e) => setRepeatEndDate(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
            </div>
          )}
          {venues?.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Venue</label>
              <select
                value={venueId}
                onChange={(e) => handleVenueSelect(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Select a venue…</option>
                {venues.map((v) => <option key={v.id} value={String(v.id)}>{v.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Location (optional)</label>
            <input type="text" value={location} onChange={(e) => { setLocation(e.target.value); setVenueId(""); }}
              placeholder="Field name, address, etc."
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
          </div>
          {members.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Who's going</label>
              <MemberCheckboxes members={members} selectedIds={memberIds} onChange={setMemberIds} />
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">SMS reminder</label>
            <select
              value={reminderHoursBefore}
              onChange={(e) => setReminderHoursBefore(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {defaultReminderHours && (
                <option value="">Season default ({defaultReminderHours}h before)</option>
              )}
              {!defaultReminderHours && <option value="">No reminder</option>}
              {REMINDER_OPTIONS.filter((o) => o.value !== "").map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
              <option value="0">No reminder (override)</option>
            </select>
          </div>
          {/* Photos */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Photos</label>
            {photos.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-2">
                {photos.map((photo) => (
                  <div key={photo.id} className="relative aspect-square">
                    <img
                      src={photo.url}
                      alt=""
                      className="w-full h-full object-cover rounded-lg cursor-pointer"
                      onClick={() => window.open(photo.url, "_blank")}
                    />
                    <button
                      type="button"
                      onClick={() => deletePhoto(photo)}
                      className="absolute top-1 right-1 bg-black bg-opacity-60 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-opacity-80 touch-manipulation"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <label className={`flex items-center gap-2 px-3 py-2 border-2 border-dashed rounded-lg cursor-pointer text-sm font-medium transition-colors ${
              uploading
                ? "border-gray-200 text-gray-400 pointer-events-none"
                : "border-gray-300 text-gray-500 hover:border-purple-400 hover:text-purple-600"
            }`}>
              <Camera size={16} />
              <span>{uploading ? "Uploading…" : photos.length > 0 ? "Add Another Photo" : "Add Photo"}</span>
              <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} disabled={uploading} />
            </label>
          </div>
        </div>
        {cancelingGame && (
          <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
            <p className="text-sm font-medium text-red-700 mb-2">Reason for cancellation (sent via SMS):</p>
            <input
              type="text"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g. field conditions, weather..."
              className="w-full border border-red-300 rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-red-400"
              autoFocus
            />
            <div className="flex gap-2">
              <button onClick={() => setCancelingGame(false)}
                className="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 touch-manipulation">
                Back
              </button>
              <button onClick={() => { onCancelGame(game, cancelReason.trim()); onClose(); }}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 touch-manipulation">
                Confirm Cancel
              </button>
            </div>
          </div>
        )}
        <div className="flex gap-2 mt-4">
          <button
            onClick={() => { if (window.confirm("Permanently delete this event? This cannot be undone.")) onDeleteGame(game); }}
            className="px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 touch-manipulation"
            title="Delete event permanently (no SMS sent)"
          >
            Delete
          </button>
          {!game.canceled && !cancelingGame ? (
            <button onClick={() => setCancelingGame(true)}
              className="px-3 py-2 bg-red-100 text-red-600 rounded-lg text-sm font-medium hover:bg-red-200 touch-manipulation">
              Cancel Game
            </button>
          ) : null}
          <button onClick={onClose}
            className="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 touch-manipulation">
            Close
          </button>
          {game.canceled ? (
            <button onClick={() => onSave(buildSaveData({ canceled: false }))}
              className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 touch-manipulation">
              Reschedule
            </button>
          ) : (
            <button onClick={handleSave}
              className="flex-1 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 touch-manipulation">
              Save
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Season Detail ──────────────────────────────────────────────────────────
// subscriptions: { [contactId]: 'games' | 'all' }
const SeasonDetail = ({ season, events, externalContacts, venues, members = [], onBack, onDelete, onAddGame, onSaveGame, onCancelGame, onRestoreGame, onDeleteGame, onSubscribe, onSendReminder, onSendCalendarLink, onBulkAdded }) => {
  const [subscriptions, setSubscriptions] = useState({});
  const [loadingSubs, setLoadingSubs] = useState(true);
  const [editingGame, setEditingGame] = useState(null);
  const [gameTitle, setGameTitle] = useState("");
  const [gameDate, setGameDate] = useState("");
  const [gameTime, setGameTime] = useState("");
  const [gameEndTime, setGameEndTime] = useState("");
  const [gameAllDay, setGameAllDay] = useState(false);
  const [gameVenueId, setGameVenueId] = useState("");
  const [gameLocation, setGameLocation] = useState("");
  const [gameEventType, setGameEventType] = useState("game");
  const [gameRepeat, setGameRepeat] = useState("");
  const [gameRepeatEndDate, setGameRepeatEndDate] = useState("");
  const [addingGame, setAddingGame] = useState(false);
  const [editingDuration, setEditingDuration] = useState(false);
  const [draftDuration, setDraftDuration] = useState(season.game_duration_minutes || "");
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState(season.name);
  const [editingReminder, setEditingReminder] = useState(false);
  const [draftReminder, setDraftReminder] = useState(season.reminder_hours_before || "");
  const [sendingReminder, setSendingReminder] = useState(null); // game id being reminded
  const [sendingCalendarLink, setSendingCalendarLink] = useState(null); // contact id being emailed
  const [sentCalendarLink, setSentCalendarLink] = useState(null); // contact id that just got sent
  const [editingMembers, setEditingMembers] = useState(false);
  const [draftMemberIds, setDraftMemberIds] = useState(season.member_ids || []);
  const [editingPracticeMembers, setEditingPracticeMembers] = useState(false);
  const [draftPracticeMemberIds, setDraftPracticeMemberIds] = useState(season.practice_member_ids || []);
  const [gameMemberIds, setGameMemberIds] = useState(season.member_ids || []);
  const [playerMemberId, setPlayerMemberId] = useState(season.player_member_id || "");
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkRows, setBulkRows] = useState(() => Array(5).fill(null).map(() => ({ date: "", time: "", eventType: "game", venueId: "", title: "" })));
  const [bulkSaving, setBulkSaving] = useState(false);

  const games = events
    .filter((e) => e.season_id === season.id)
    .sort((a, b) => (a.date > b.date ? 1 : -1));

  // Show venues with no sport tags (universal) + venues tagged with this season's sport
  const filteredVenues = (venues || []).filter((v) => {
    if (!season.sport) return true;
    if (!v.sports?.length) return true;
    return v.sports.includes(season.sport);
  });

  useEffect(() => {
    loadSubscriptions();
  }, [season.id]); // eslint-disable-line

  const loadSubscriptions = async () => {
    setLoadingSubs(true);
    try {
      const { data, error } = await supabase
        .from("season_subscriptions")
        .select("external_contact_id, notify_type")
        .eq("season_id", season.id);
      if (error) throw error;
      const map = {};
      (data || []).forEach((r) => { map[r.external_contact_id] = r.notify_type || "games"; });
      setSubscriptions(map);
    } catch (err) {
      console.error("Error loading subscriptions:", err);
    } finally {
      setLoadingSubs(false);
    }
  };

  const subscribeContact = async (contact, notifyType) => {
    const contactId = contact.id;
    const current = subscriptions[contactId];
    if (current) {
      // Already subscribed — just update notify_type
      setSubscriptions((prev) => ({ ...prev, [contactId]: notifyType }));
      await supabase
        .from("season_subscriptions")
        .update({ notify_type: notifyType })
        .eq("season_id", season.id)
        .eq("external_contact_id", contactId);
    } else {
      // New subscription — insert, send welcome SMS, then send calendar link
      setSubscriptions((prev) => ({ ...prev, [contactId]: notifyType }));
      await supabase.from("season_subscriptions").insert([{
        season_id: season.id,
        external_contact_id: contactId,
        notify_type: notifyType,
      }]);
      const today = new Date().toISOString().slice(0, 10);
      const upcoming = games
        .filter((e) => !e.canceled && e.date >= today && (notifyType === "all" || e.event_type !== "practice"))
        .sort((a, b) => (a.date > b.date ? 1 : -1));
      await onSubscribe(contact, notifyType, season, upcoming);
      if (onSendCalendarLink) await onSendCalendarLink(contact, season, notifyType);
    }
  };

  const unsubscribeContact = async (contactId) => {
    setSubscriptions((prev) => {
      const next = { ...prev };
      delete next[contactId];
      return next;
    });
    await supabase
      .from("season_subscriptions")
      .delete()
      .eq("season_id", season.id)
      .eq("external_contact_id", contactId);
  };

  const saveName = async () => {
    if (!draftName.trim()) return;
    await supabase.from("seasons").update({ name: draftName.trim() }).eq("id", season.id);
    season.name = draftName.trim(); // reflect locally until parent re-renders
    setEditingName(false);
  };

  const saveDuration = async (val, updateGames = false) => {
    const minutes = val ? parseInt(val) : null;
    await supabase.from("seasons").update({ game_duration_minutes: minutes }).eq("id", season.id);
    season.game_duration_minutes = minutes;
    if (updateGames && minutes) {
      // Recalculate end_time for every game that has a start time
      await Promise.all(
        games
          .filter((g) => g.time && !g.all_day)
          .map((g) => {
            const newEndTime = addMinutes(g.time.substring(0, 5), minutes);
            return supabase.from("events").update({ end_time: newEndTime }).eq("id", g.id);
          })
      );
    }
    setEditingDuration(false);
  };

  const saveReminder = async (val) => {
    const hours = val ? parseInt(val) : null;
    await supabase.from("seasons").update({ reminder_hours_before: hours }).eq("id", season.id);
    season.reminder_hours_before = hours;
    setEditingReminder(false);
  };

  const saveSeasonMembers = async (updateGames = false) => {
    const newIds = draftMemberIds.length ? draftMemberIds : null;
    await supabase.from("seasons").update({ member_ids: newIds }).eq("id", season.id);
    season.member_ids = newIds;
    setGameMemberIds(draftMemberIds);
    if (updateGames) {
      await supabase.from("events")
        .update({ member_ids: newIds, member_id: null })
        .eq("season_id", season.id)
        .neq("event_type", "practice");
    }
    setEditingMembers(false);
  };

  const savePracticeMembers = async (updatePractices = false) => {
    const newIds = draftPracticeMemberIds.length ? draftPracticeMemberIds : null;
    await supabase.from("seasons").update({ practice_member_ids: newIds }).eq("id", season.id);
    season.practice_member_ids = newIds;
    if (updatePractices) {
      await supabase.from("events")
        .update({ member_ids: newIds, member_id: null })
        .eq("season_id", season.id)
        .eq("event_type", "practice");
    }
    setEditingPracticeMembers(false);
  };

  const handleAddGame = async (e) => {
    e.preventDefault();
    if (!gameTitle.trim() || !gameDate) return;
    setAddingGame(true);
    const computedEndTime = (!gameAllDay && gameTime && season.game_duration_minutes && !gameEndTime)
      ? addMinutes(gameTime, season.game_duration_minutes)
      : null;
    await onAddGame({
      title: gameTitle.trim(),
      date: gameDate,
      time: gameAllDay ? null : gameTime || null,
      end_time: gameAllDay ? null : (gameEndTime || computedEndTime || null),
      all_day: gameAllDay,
      location: gameLocation.trim() || null,
      venue_id: gameVenueId ? parseInt(gameVenueId) : null,
      season_id: season.id,
      event_type: gameEventType,
      member_ids: gameMemberIds.length ? gameMemberIds : null,
      // note: gameMemberIds is already set to practice defaults when eventType is practice
      member_id: null,
      repeat: gameRepeat || null,
      repeat_end_date: gameRepeat && gameRepeatEndDate ? gameRepeatEndDate : null,
    });
    setGameTitle("");
    setGameDate("");
    setGameTime("");
    setGameEndTime("");
    setGameAllDay(false);
    setGameVenueId("");
    setGameLocation("");
    setGameEventType("game");
    setGameRepeat("");
    setGameRepeatEndDate("");
    setAddingGame(false);
  };

  const handleBulkAdd = async () => {
    const validRows = bulkRows.filter((r) => r.date);
    if (!validRows.length) return;
    setBulkSaving(true);
    const insertData = validRows.map((row) => {
      const venue = row.venueId ? filteredVenues.find((v) => String(v.id) === row.venueId) : null;
      const endTime = row.time && season.game_duration_minutes ? addMinutes(row.time, season.game_duration_minutes) : null;
      return {
        title: row.title.trim() || `${season.name} ${row.eventType === "practice" ? "Practice" : "Game"}`,
        date: row.date,
        time: row.time || null,
        end_time: endTime,
        all_day: !row.time,
        venue_id: venue ? parseInt(row.venueId) : null,
        location: venue?.address || null,
        season_id: season.id,
        event_type: row.eventType,
        member_ids: season.member_ids?.length ? season.member_ids : null,
        member_id: null,
        canceled: false,
      };
    });
    await supabase.from("events").insert(insertData);
    if (onBulkAdded) await onBulkAdded(validRows.length, season);
    setBulkRows(Array(5).fill(null).map(() => ({ date: "", time: "", eventType: "game", venueId: "" })));
    setBulkMode(false);
    setBulkSaving(false);
  };

  const updateBulkRow = (i, field, value) =>
    setBulkRows((prev) => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));

  const eventCount = games.length;
  const gameCount = games.filter((e) => e.event_type !== "practice").length;
  const practiceCount = games.filter((e) => e.event_type === "practice").length;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg touch-manipulation">
          <ChevronLeft size={20} />
        </button>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-2xl flex-shrink-0">{getSportEmoji(season.sport)}</span>
          <div className="min-w-0 flex-1">
            {editingName ? (
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditingName(false); }}
                  autoFocus
                  className="flex-1 border rounded px-2 py-0.5 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <button onClick={saveName} className="text-purple-600 text-xs font-medium touch-manipulation px-1">Save</button>
                <button onClick={() => setEditingName(false)} className="text-gray-400 text-xs touch-manipulation px-1">✕</button>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <h3 className="font-bold text-gray-800 truncate">{season.name}</h3>
                <button onClick={() => { setDraftName(season.name); setEditingName(true); }} className="text-xs text-gray-400 hover:text-purple-600 touch-manipulation flex-shrink-0">✏️</button>
              </div>
            )}
            {season.sport && <p className="text-xs text-gray-500">{season.sport}</p>}
          </div>
          <div className="w-3 h-3 rounded-full flex-shrink-0 ml-1" style={{ backgroundColor: season.color }} />
        </div>
        <button
          onClick={() => { if (window.confirm(`Delete "${season.name}"? The events will remain on the calendar.`)) onDelete(season.id); }}
          className="p-2 text-gray-300 hover:text-red-500 touch-manipulation flex-shrink-0"
        >
          <Trash2 size={18} />
        </button>
      </div>

      {/* Subscribers */}
      {externalContacts.length > 0 && (
        <div className="mb-5 p-3 bg-blue-50 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-700 mb-1">Subscribers</h4>
          <p className="text-xs text-gray-500 mb-3">
            Toggle contacts on to subscribe. Choose whether they get games only or all events. Email contacts receive the full schedule + a calendar subscription link. Phone-only contacts get an SMS schedule.
          </p>
          {loadingSubs ? (
            <p className="text-xs text-gray-400">Loading...</p>
          ) : (
            <div className="space-y-2">
              {externalContacts.map((contact) => {
                const notifyType = subscriptions[contact.id];
                const isOn = !!notifyType;
                return (
                  <div key={contact.id} className="flex items-center gap-2">
                    {/* On/off toggle */}
                    <button
                      onClick={() => isOn ? unsubscribeContact(contact.id) : subscribeContact(contact, "games")}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors touch-manipulation ${
                        isOn ? "bg-blue-600" : "bg-gray-200"
                      }`}
                    >
                      <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${isOn ? "translate-x-5" : "translate-x-0"}`} />
                    </button>
                    <span className="text-sm text-gray-700 flex-1">📱 {contact.name}</span>
                    {/* Games / All selector — only shown when subscribed */}
                    {isOn && (
                      <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs font-medium">
                        <button
                          onClick={() => subscribeContact(contact, "games")}
                          className={`px-2.5 py-1 touch-manipulation ${notifyType === "games" ? "bg-blue-600 text-white" : "bg-white text-gray-500"}`}
                        >
                          Games
                        </button>
                        <button
                          onClick={() => subscribeContact(contact, "all")}
                          className={`px-2.5 py-1 touch-manipulation border-l border-gray-200 ${notifyType === "all" ? "bg-purple-600 text-white" : "bg-white text-gray-500"}`}
                        >
                          All
                        </button>
                      </div>
                    )}
                    {/* Send calendar link */}
                    {isOn && onSendCalendarLink && (
                      <button
                        onClick={async () => {
                          setSendingCalendarLink(contact.id);
                          await onSendCalendarLink(contact, season, notifyType);
                          setSendingCalendarLink(null);
                          setSentCalendarLink(contact.id);
                          setTimeout(() => setSentCalendarLink(null), 2000);
                        }}
                        disabled={sendingCalendarLink === contact.id}
                        title={contact.email ? "Email schedule + calendar link" : "Send calendar link via SMS"}
                        className="text-lg touch-manipulation leading-none disabled:opacity-40"
                      >
                        {sendingCalendarLink === contact.id ? "⏳" : sentCalendarLink === contact.id ? "✅" : "📅"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Game duration */}
      <div className="mb-4 flex items-center gap-2 text-sm">
        <span className="text-gray-500">Default duration:</span>
        {editingDuration ? (
          <>
            <select
              value={draftDuration}
              onChange={(e) => setDraftDuration(e.target.value)}
              autoFocus
              className="border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {DURATION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button onClick={() => saveDuration(draftDuration, false)} className="text-purple-600 font-medium hover:text-purple-800 touch-manipulation px-1">Save default</button>
            <button onClick={() => saveDuration(draftDuration, true)} className="text-purple-800 font-medium hover:text-purple-900 touch-manipulation px-1">+ update all</button>
            <button onClick={() => setEditingDuration(false)} className="text-gray-400 hover:text-gray-600 touch-manipulation px-1">Cancel</button>
          </>
        ) : (
          <>
            <span className="font-medium text-gray-700">{fmtDuration(season.game_duration_minutes) || "None"}</span>
            <button onClick={() => { setDraftDuration(season.game_duration_minutes || ""); setEditingDuration(true); }}
              className="text-xs text-purple-600 hover:text-purple-800 touch-manipulation">Edit</button>
          </>
        )}
      </div>

      {/* Season player (used in SMS prefix) */}
      {members.length > 0 && (
        <div className="flex items-center gap-2 text-sm mb-3">
          <span className="text-gray-500">Player (SMS):</span>
          <select
            value={playerMemberId}
            onChange={async (e) => {
              const val = e.target.value ? Number(e.target.value) : null;
              setPlayerMemberId(e.target.value);
              await supabase.from("seasons").update({ player_member_id: val }).eq("id", season.id);
              season.player_member_id = val;
            }}
            className="text-sm border border-gray-200 rounded px-2 py-0.5 text-gray-700 touch-manipulation"
          >
            <option value="">— none —</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Season members */}
      {members.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Game members:</span>
            {editingMembers ? null : (
              <>
                <MemberDots members={members} memberIds={season.member_ids || []} />
                <button onClick={() => { setDraftMemberIds(season.member_ids || []); setEditingMembers(true); }}
                  className="text-xs text-purple-600 hover:text-purple-800 touch-manipulation">Edit</button>
              </>
            )}
          </div>
          {editingMembers && (
            <div className="mt-2 space-y-2">
              <MemberCheckboxes members={members} selectedIds={draftMemberIds} onChange={setDraftMemberIds} />
              <div className="flex flex-wrap gap-2">
                <button onClick={() => saveSeasonMembers(false)} className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700 touch-manipulation">Save default</button>
                <button onClick={() => saveSeasonMembers(true)} className="px-3 py-1.5 bg-purple-800 text-white rounded-lg text-xs font-medium hover:bg-purple-900 touch-manipulation">Save + update all</button>
                <button onClick={() => setEditingMembers(false)} className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs hover:bg-gray-200 touch-manipulation">Cancel</button>
              </div>
            </div>
          )}
          {/* Practice members */}
          <div className="flex items-center gap-2 text-sm mt-2">
            <span className="text-gray-500">Practice members:</span>
            {editingPracticeMembers ? null : (
              <>
                <MemberDots members={members} memberIds={season.practice_member_ids || []} />
                <button onClick={() => { setDraftPracticeMemberIds(season.practice_member_ids || []); setEditingPracticeMembers(true); }}
                  className="text-xs text-purple-600 hover:text-purple-800 touch-manipulation">Edit</button>
              </>
            )}
          </div>
          {editingPracticeMembers && (
            <div className="mt-2 space-y-2">
              <MemberCheckboxes members={members} selectedIds={draftPracticeMemberIds} onChange={setDraftPracticeMemberIds} />
              <div className="flex flex-wrap gap-2">
                <button onClick={() => savePracticeMembers(false)} className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700 touch-manipulation">Save default</button>
                <button onClick={() => savePracticeMembers(true)} className="px-3 py-1.5 bg-purple-800 text-white rounded-lg text-xs font-medium hover:bg-purple-900 touch-manipulation">Save + update all practices</button>
                <button onClick={() => setEditingPracticeMembers(false)} className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs hover:bg-gray-200 touch-manipulation">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* SMS Reminder timing */}
      <div className="mb-4 flex items-center gap-2 text-sm">
        <span className="text-gray-500">SMS reminder:</span>
        {editingReminder ? (
          <>
            <select
              value={draftReminder}
              onChange={(e) => setDraftReminder(e.target.value)}
              autoFocus
              className="border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {REMINDER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button onClick={() => saveReminder(draftReminder)} className="text-purple-600 font-medium hover:text-purple-800 touch-manipulation px-1">Save</button>
            <button onClick={() => setEditingReminder(false)} className="text-gray-400 hover:text-gray-600 touch-manipulation px-1">Cancel</button>
          </>
        ) : (
          <>
            <span className="font-medium text-gray-700">
              {season.reminder_hours_before ? `${season.reminder_hours_before}h before` : "None"}
            </span>
            <button onClick={() => { setDraftReminder(season.reminder_hours_before || ""); setEditingReminder(true); }}
              className="text-xs text-purple-600 hover:text-purple-800 touch-manipulation">Edit</button>
          </>
        )}
      </div>

      {/* Add Game/Practice form */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-gray-700">Add Event</h4>
          <button
            type="button"
            onClick={() => setBulkMode((v) => !v)}
            className="text-xs text-purple-600 hover:text-purple-800 touch-manipulation font-medium"
          >
            {bulkMode ? "← Single add" : "Bulk add ↓"}
          </button>
        </div>

        {bulkMode ? (
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <p className="text-xs text-gray-500">Leave title blank to auto-generate from season name. Tap + to add more rows.</p>
            <div className="space-y-2">
              {bulkRows.map((row, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-lg p-2 space-y-1.5">
                  {/* Row 1: date + time + delete */}
                  <div className="flex gap-1 items-center">
                    <input
                      type="date"
                      value={row.date}
                      onChange={(e) => updateBulkRow(i, "date", e.target.value)}
                      className="flex-1 border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                    />
                    <select
                      value={row.time}
                      onChange={(e) => updateBulkRow(i, "time", e.target.value)}
                      className="w-28 border rounded px-1 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                    >
                      {BULK_TIME_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setBulkRows((prev) => prev.filter((_, idx) => idx !== i))}
                      className="text-gray-300 hover:text-red-400 touch-manipulation p-1 flex-shrink-0"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  {/* Row 2: title + game/practice toggle + venue */}
                  <div className="flex gap-1 items-center">
                    <input
                      type="text"
                      value={row.title}
                      onChange={(e) => updateBulkRow(i, "title", e.target.value)}
                      placeholder="vs. Tigers, @ Riverside… (optional)"
                      className="flex-1 border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                    />
                    <div className="flex rounded border border-gray-200 overflow-hidden text-xs font-medium flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => updateBulkRow(i, "eventType", "game")}
                        className={`px-2 py-1 touch-manipulation ${row.eventType === "game" ? "bg-purple-600 text-white" : "bg-white text-gray-500"}`}
                      >
                        Game
                      </button>
                      <button
                        type="button"
                        onClick={() => updateBulkRow(i, "eventType", "practice")}
                        className={`px-2 py-1 touch-manipulation border-l ${row.eventType === "practice" ? "bg-blue-600 text-white" : "bg-white text-gray-500"}`}
                      >
                        Practice
                      </button>
                    </div>
                    {filteredVenues.length > 0 && (
                      <select
                        value={row.venueId}
                        onChange={(e) => updateBulkRow(i, "venueId", e.target.value)}
                        className="w-24 border rounded px-1 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                      >
                        <option value="">Venue…</option>
                        {filteredVenues.map((v) => <option key={v.id} value={String(v.id)}>{v.name}</option>)}
                      </select>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setBulkRows((prev) => [...prev, { date: "", time: "", eventType: "game", venueId: "", title: "" }])}
                className="text-xs text-purple-600 hover:text-purple-800 touch-manipulation font-medium"
              >
                + Add row
              </button>
              <button
                type="button"
                onClick={handleBulkAdd}
                disabled={bulkSaving || !bulkRows.some((r) => r.date)}
                className="ml-auto px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 touch-manipulation disabled:opacity-50"
              >
                {bulkSaving ? "Saving…" : `Save ${bulkRows.filter((r) => r.date).length || ""} Events`}
              </button>
            </div>
          </div>
        ) : (
        <form onSubmit={handleAddGame} className="bg-gray-50 rounded-lg p-3 space-y-2">
          {/* Game / Practice toggle */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setGameEventType("game"); setGameMemberIds(season.member_ids || []); }}
              className={`flex-1 py-1.5 rounded-lg text-sm font-medium border-2 touch-manipulation ${
                gameEventType === "game" ? "border-purple-500 bg-purple-50 text-purple-700" : "border-gray-200 text-gray-400"
              }`}
            >
              🏆 Game
            </button>
            <button
              type="button"
              onClick={() => { setGameEventType("practice"); setGameMemberIds(season.practice_member_ids || season.member_ids || []); }}
              className={`flex-1 py-1.5 rounded-lg text-sm font-medium border-2 touch-manipulation ${
                gameEventType === "practice" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-gray-200 text-gray-400"
              }`}
            >
              🏃 Practice
            </button>
          </div>
          <input
            type="text"
            value={gameTitle}
            onChange={(e) => setGameTitle(e.target.value)}
            placeholder={gameEventType === "practice" ? "Practice title (e.g. Practice)" : "Title (e.g. vs. Tigers, @ Riverside)"}
            required
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <input
            type="date"
            value={gameDate}
            onChange={(e) => setGameDate(e.target.value)}
            required
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <label className="flex items-center gap-2 cursor-pointer touch-manipulation">
            <input
              type="checkbox"
              checked={gameAllDay}
              onChange={(e) => setGameAllDay(e.target.checked)}
              className="rounded w-4 h-4"
            />
            <span className="text-xs text-gray-600">All day / TBD time</span>
          </label>
          {!gameAllDay && (
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">Start time</label>
                <select
                  value={gameTime}
                  onChange={(e) => {
                    setGameTime(e.target.value);
                    if (season.game_duration_minutes && e.target.value) {
                      setGameEndTime(addMinutes(e.target.value, season.game_duration_minutes));
                    }
                  }}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  {BULK_TIME_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label || "No time"}</option>)}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">End time</label>
                <select
                  value={gameEndTime}
                  onChange={(e) => setGameEndTime(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  {BULK_TIME_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label || "No time"}</option>)}
                </select>
              </div>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Repeat</label>
            <select
              value={gameRepeat}
              onChange={(e) => setGameRepeat(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">No repeat</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Every 2 weeks</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          {gameRepeat && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Repeat until</label>
              <input
                type="date"
                value={gameRepeatEndDate}
                onChange={(e) => setGameRepeatEndDate(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          )}
          {filteredVenues.length > 0 && (
            <select
              value={gameVenueId}
              onChange={(e) => {
                setGameVenueId(e.target.value);
                if (e.target.value) {
                  const v = filteredVenues.find((v) => String(v.id) === e.target.value);
                  if (v) setGameLocation(v.address || "");
                }
              }}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select a venue…</option>
              {filteredVenues.map((v) => <option key={v.id} value={String(v.id)}>{v.name}</option>)}
            </select>
          )}
          <input
            type="text"
            value={gameLocation}
            onChange={(e) => { setGameLocation(e.target.value); setGameVenueId(""); }}
            placeholder="Location (optional)"
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          {members.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Who's going</p>
              <MemberCheckboxes members={members} selectedIds={gameMemberIds} onChange={setGameMemberIds} />
            </div>
          )}
          <button
            type="submit"
            disabled={addingGame}
            className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 touch-manipulation disabled:opacity-50"
          >
            <Plus size={14} />
            Add {gameEventType === "practice" ? "Practice" : "Game"}
          </button>
        </form>
        )}
      </div>

      {/* Events list */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">
          {eventCount} Event{eventCount !== 1 ? "s" : ""}
          {gameCount > 0 && practiceCount > 0 && (
            <span className="font-normal text-gray-400 ml-1">({gameCount}G / {practiceCount}P)</span>
          )}
        </h4>
        {games.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">No events yet. Add games and practices above!</p>
        ) : (
          <div className="space-y-2">
            {games.map((game) => {
              const isPractice = game.event_type === "practice";
              return (
                <div
                  key={game.id}
                  className={`flex items-start gap-3 border rounded-lg px-3 py-2.5 ${
                    game.canceled ? "border-red-100 bg-red-50" : "bg-white border-gray-100"
                  }`}
                >
                  <span className="text-base flex-shrink-0 mt-0.5">{isPractice ? "🏃" : "🏆"}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${game.canceled ? "line-through text-gray-400" : "text-gray-800"}`}>
                      {game.canceled && (
                        <span className="not-italic no-underline text-red-400 font-normal text-xs mr-1">[CANCELED]</span>
                      )}
                      {game.title}
                    </p>
                    <p className="text-xs text-gray-400">
                      {fmtDate(game.date)}
                      {game.time && !game.all_day ? ` at ${fmtTime(game.time)}` : ""}
                      {(() => {
                        const venueName = game.venue_id
                          ? venues?.find((v) => v.id === game.venue_id)?.name
                          : null;
                        const display = venueName || game.location;
                        return display ? ` · ${display}` : "";
                      })()}
                    </p>
                  </div>
                  {game.canceled ? (
                    <button
                      onClick={() => setEditingGame(game)}
                      className="text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-lg touch-manipulation px-2 py-1 flex-shrink-0"
                    >
                      Reschedule
                    </button>
                  ) : (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {onSendReminder && (
                        <button
                          onClick={async () => {
                            setSendingReminder(game.id);
                            await onSendReminder(game, season);
                            setSendingReminder(null);
                          }}
                          disabled={sendingReminder === game.id}
                          title="Send reminder SMS now"
                          className="text-xs text-blue-500 hover:text-blue-700 touch-manipulation px-2 py-1 disabled:opacity-40"
                        >
                          {sendingReminder === game.id ? "..." : "📣"}
                        </button>
                      )}
                      <button
                        onClick={() => setEditingGame(game)}
                        className="text-xs text-purple-600 hover:text-purple-800 touch-manipulation px-2 py-1"
                      >
                        Edit
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editingGame && (
        <EditGameModal
          game={editingGame}
          venues={filteredVenues}
          gameDurationMinutes={season.game_duration_minutes}
          defaultReminderHours={season.reminder_hours_before}
          members={members}
          defaultMemberIds={editingGame?.event_type === "practice" ? (season.practice_member_ids || season.member_ids || []) : (season.member_ids || [])}
          onClose={() => setEditingGame(null)}
          onSave={(data) => { onSaveGame(editingGame, data); setEditingGame(null); }}
          onCancelGame={(game, reason) => { onCancelGame(game, reason); setEditingGame(null); }}
          onRestoreGame={(game) => { onRestoreGame(game); setEditingGame(null); }}
          onDeleteGame={(game) => { onDeleteGame(game); setEditingGame(null); }}
        />
      )}
    </div>
  );
};

// ── Main SeasonsView ───────────────────────────────────────────────────────
const SeasonsView = ({
  seasons,
  externalContacts,
  venues,
  events,
  members = [],
  onSeasonCreate,
  onSeasonDelete,
  onAddGame,
  onSaveGame,
  onCancelGame,
  onRestoreGame,
  onDeleteGame,
  onSubscribe,
  onSendReminder,
  onSendCalendarLink,
  onBulkAdded,
}) => {
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newSport, setNewSport] = useState("");
  const [newColor, setNewColor] = useState(PRESET_COLORS[4]);
  const [newDuration, setNewDuration] = useState(90);
  const [newMemberIds, setNewMemberIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setSaving(true);
    await onSeasonCreate({ name: newName.trim(), sport: newSport || null, color: newColor, game_duration_minutes: newDuration || null, member_ids: newMemberIds.length ? newMemberIds : null });
    setNewName("");
    setNewSport("");
    setNewColor(PRESET_COLORS[4]);
    setNewDuration(90);
    setNewMemberIds([]);
    setShowForm(false);
    setSaving(false);
  };

  if (selectedSeason) {
    const freshSeason = seasons.find((s) => s.id === selectedSeason.id) || selectedSeason;
    return (
      <SeasonDetail
        season={freshSeason}
        events={events}
        externalContacts={externalContacts}
        venues={venues}
        members={members}
        onBack={() => setSelectedSeason(null)}
        onDelete={(id) => { onSeasonDelete(id); setSelectedSeason(null); }}
        onAddGame={onAddGame}
        onSaveGame={onSaveGame}
        onCancelGame={onCancelGame}
        onRestoreGame={onRestoreGame}
        onDeleteGame={onDeleteGame}
        onSubscribe={onSubscribe}
        onSendReminder={onSendReminder}
        onSendCalendarLink={onSendCalendarLink}
        onBulkAdded={onBulkAdded}
      />
    );
  }

  return (
    <div>
      {seasons.length === 0 && !showForm && (
        <p className="text-sm text-gray-400 text-center py-6">
          No seasons yet. Create one to manage your team's schedule and send updates.
        </p>
      )}

      {seasons.length > 0 && (
        <div className="space-y-2 mb-4">
          {seasons.map((season) => {
            const seasonEvents = events.filter((e) => e.season_id === season.id);
            const gameCount = seasonEvents.filter((e) => e.event_type !== "practice").length;
            const practiceCount = seasonEvents.filter((e) => e.event_type === "practice").length;
            const canceledCount = seasonEvents.filter((e) => e.canceled).length;
            return (
              <button
                key={season.id}
                onClick={() => setSelectedSeason(season)}
                className="w-full flex items-center gap-3 bg-white border border-gray-100 rounded-lg px-4 py-3 hover:bg-gray-50 touch-manipulation text-left"
              >
                <span className="text-2xl">{getSportEmoji(season.sport)}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-800">{season.name}</p>
                  <p className="text-xs text-gray-400">
                    {season.sport || "Season"} ·{" "}
                    {gameCount} game{gameCount !== 1 ? "s" : ""}
                    {practiceCount > 0 && `, ${practiceCount} practice${practiceCount !== 1 ? "s" : ""}`}
                    {canceledCount > 0 && `, ${canceledCount} canceled`}
                  </p>
                </div>
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: season.color }} />
                <ChevronLeft size={16} className="text-gray-300 -rotate-180 flex-shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      {showForm ? (
        <form onSubmit={handleCreate} className="bg-gray-50 rounded-lg p-4 space-y-3">
          <h4 className="text-sm font-semibold text-gray-700">New Season</h4>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Season name (e.g. Spring Baseball 2026)"
            required
            autoFocus
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <div>
            <input
              type="text"
              value={newSport}
              onChange={(e) => setNewSport(e.target.value)}
              placeholder="Sport (e.g. Baseball)"
              list="seasons-sports-list"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <datalist id="seasons-sports-list">
              {Object.keys(SPORT_EMOJIS).map((s) => <option key={s} value={s} />)}
            </datalist>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-2">Color</p>
            <div className="flex gap-2 flex-wrap">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setNewColor(c)}
                  className={`w-7 h-7 rounded-full touch-manipulation border-2 ${newColor === c ? "border-gray-800 scale-110" : "border-transparent"}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Default game duration</label>
            <select
              value={newDuration}
              onChange={(e) => setNewDuration(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {DURATION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          {members.length > 0 && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Family members (default for all games)</label>
              <MemberCheckboxes members={members} selectedIds={newMemberIds} onChange={setNewMemberIds} />
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="flex-1 py-2 bg-gray-200 text-gray-600 rounded-lg text-sm font-medium touch-manipulation"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 touch-manipulation disabled:opacity-50"
            >
              {saving ? "Creating..." : "Create Season"}
            </button>
          </div>
        </form>
      ) : (
        <button
          onClick={() => setShowForm(true)}
          className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-200 rounded-lg text-gray-500 hover:border-purple-400 hover:text-purple-600 text-sm font-medium touch-manipulation"
        >
          <Plus size={16} />
          New Season
        </button>
      )}
    </div>
  );
};

export default SeasonsView;
